import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { useState } from "react";
import { colors } from "../design-system";

type AppPressableProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style"> & {
  style?: CSSProperties;
  /** Style additionnel appliqué pendant l'appui (fusionné par-dessus `style`). */
  pressedStyle?: CSSProperties;
  children?: ReactNode;
};

/**
 * Zone pressable de l'app.
 *
 * ## Pourquoi ce composant existait, et pourquoi il reste
 * En natif, il contournait un bug précis : le runtime nativewind interceptait
 * chaque `Pressable` et détruisait le style de repos quand celui-ci était une
 * FONCTION (`style={({ pressed }) => …}`), ce qui a cassé la Home sur device.
 * Ce bug n'existe pas ici — nativewind a disparu avec React Native.
 *
 * Le composant est conservé pour la SECONDE raison, qui elle tient toujours :
 * offrir un feedback d'appui unique à toute l'app (calque navy à 8 %,
 * `colors.pressOverlay`) plutôt qu'une opacité ad hoc par composant.
 *
 * ## Ce que le web apporte gratuitement
 * `<button>` donne l'accessibilité que `Pressable` n'avait pas : focus au
 * clavier, activation par Entrée et Espace, rôle annoncé aux lecteurs d'écran,
 * état `disabled` natif. C'est la raison de rendre un vrai `<button>` plutôt
 * qu'un `<div onClick>`.
 *
 * ⚠️ Un `<button>` ne peut pas contenir d'élément interactif (lien, autre
 * bouton) : c'est invalide en HTML et casse la navigation clavier. Si un écran
 * du Lot 6 en a besoin, utiliser un conteneur non interactif et rendre les
 * enfants cliquables individuellement.
 */
export function AppPressable({
  style,
  pressedStyle,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  children,
  ...props
}: AppPressableProps) {
  const [pressed, setPressed] = useState(false);

  return (
    <button
      type="button"
      {...props}
      onPointerDown={(e) => {
        setPressed(true);
        onPointerDown?.(e);
      }}
      onPointerUp={(e) => {
        setPressed(false);
        onPointerUp?.(e);
      }}
      // Sans ça, un appui commencé puis relâché HORS du bouton laisse l'état
      // « pressé » collé — le pointerup part alors à un autre élément.
      onPointerLeave={(e) => {
        setPressed(false);
        onPointerLeave?.(e);
      }}
      style={{
        // Un <button> arrive avec fond, bordure et police d'agent utilisateur :
        // on remet à zéro pour que `style` parte d'une surface neutre, comme le
        // faisait une View.
        position: "relative",
        appearance: "none",
        background: "none",
        border: "none",
        padding: 0,
        // Les marges sont réinitialisées par Tailwind Preflight. Une valeur
        // inline ici empêcherait les espacements `space-y-*` et `mt-*`.
        font: "inherit",
        color: "inherit",
        textAlign: "inherit",
        cursor: props.disabled ? "default" : "pointer",
        ...style,
        ...(pressed ? pressedStyle : null),
      }}
    >
      {children}
      {pressed && !pressedStyle && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            // Le calque épouse l'arrondi du conteneur pour ne pas déborder des
            // coins — `inherit` évite de devoir relire le style.
            borderRadius: "inherit",
            backgroundColor: colors.pressOverlay,
          }}
        />
      )}
    </button>
  );
}
